import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SITE_DOC_KIND_PATH, type SiteDocDetail } from '@symphenia/shared-types'
import { FeldZeile, feldKlasse, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { updateSiteDoc } from '../api/site-docs.api'

const alsDatensatz = (d: SiteDocDetail): Datensatz => ({ title: d.title, documentDate: d.documentDate.slice(0, 10), updatedAt: d.updatedAt })

/**
 * AP-36 Fall 1 — **Kopf eines ERP-Entwurfs**: Titel und Datum, feldweise beim Verlassen, ohne Knopf.
 * Ab der Nummer ist das Datum gesperrt (das Jahr gehört zum Zähler, 7.).
 */
export function KopfFelder({ dok, onGeaendert }: { dok: SiteDocDetail; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const daten = useMemo(() => alsDatensatz(dok), [dok])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `siteDoc:${dok.id}`,
    bezeichnung: `${t(`siteDocs.kinds.${dok.kind}`)} ${dok.number ?? ''}`.trim(),
    daten,
    schreibe: async (patch, stand) => {
      const neu = await updateSiteDoc(dok.kind, dok.id, patch, mitStand(stand))
      onGeaendert(neu)
      return alsDatensatz(neu)
    },
    keepalive: { url: `/site-docs/${SITE_DOC_KIND_PATH[dok.kind]}/${dok.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const titel = useFeld<string | null>(stelle, 'title', { label: t('siteDocs.columns.title'), pflicht: true })
  const datum = useFeld<string | null>(stelle, 'documentDate', { art: 'datum', label: t('siteDocs.preview.date'), pflicht: true, gesperrt: !!dok.number })

  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
      <FeldZeile label={t('siteDocs.columns.title')} status={titel}>
        <input className={feldKlasse} {...titel.props} />
      </FeldZeile>
      <FeldZeile label={t('siteDocs.preview.date')} status={datum} hilfe={dok.number ? t('siteDocs.detail.dateLocked') : undefined}>
        <input type="date" className={feldKlasse} {...datum.props} />
      </FeldZeile>
    </div>
  )
}
